import { useEffect, useRef, useState } from 'react';

/** ЭКГ-дорожка голосового из макета: три повтора одного «удара», viewBox 0 0 300 40. */
export const WAVE_POINTS = (() => {
  const beat: [number, number][] = [
    [0, 20],
    [16, 20],
    [22, 14],
    [28, 20],
    [40, 20],
    [43, 24],
    [46, 2],
    [49, 38],
    [52, 17],
    [55, 20],
    [64, 20],
    [70, 11],
    [76, 20],
    [100, 20],
  ];
  const pts: string[] = [];
  for (let k = 0; k < 3; k++) {
    beat.forEach(([x, y], i) => {
      if (k && i === 0) {
        return;
      }
      pts.push(`${x + k * 100},${y}`);
    });
  }
  return pts.join(' ');
})();

const IMAGE_RE = /\.(jpg|jpeg|png|gif|webp|bmp|heic)$/i;
const VIDEO_RE = /\.(mp4|mov|webm|mkv|avi)$/i;
const VOICE_RE = /^voice-\d+\./i;

export function isVoiceName(name: string) {
  return VOICE_RE.test(name);
}

export function isImageName(name: string) {
  return IMAGE_RE.test(name);
}

export function isVideoName(name: string) {
  return VIDEO_RE.test(name);
}

/** Медиа = картинка или видео (и это не голосовое). */
export function isMediaName(name: string) {
  return !isVoiceName(name) && (isImageName(name) || isVideoName(name));
}

/** «M:SS» из миллисекунд. */
export function formatVoiceLength(ms: number) {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function PlayGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="#FFFFFF"
      aria-hidden
    >
      <path d="M5 3.4l7 4.6-7 4.6z" />
    </svg>
  );
}

/** Плитка картинки или видео: 148×96, имя на затемнённой подложке, × в углу. */
export function RMediaTile({
  name,
  video,
  previewUrl,
  onRemove,
}: {
  name: string;
  video: boolean;
  previewUrl?: string | null;
  onRemove: () => void;
}) {
  return (
    <span
      className="relative flex h-[96px] w-[148px] items-end overflow-hidden rounded-[11px] border border-[rgba(59,77,127,0.14)]"
      style={{
        background:
          'repeating-linear-gradient(45deg, #F1F3F8, #F1F3F8 10px, #E9ECF4 10px, #E9ECF4 20px)',
      }}
    >
      {previewUrl && !video ? (
        <img
          src={previewUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
      ) : null}
      <span className="relative w-full truncate bg-[rgba(20,30,49,0.55)] px-[8px] py-[5px] text-[10px] font-semibold text-white">
        {name}
      </span>
      {video ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-[rgba(20,30,49,0.6)]">
            <PlayGlyph />
          </span>
        </span>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-[6px] top-[6px] flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[rgba(20,30,49,0.55)] text-[11px] leading-none text-white"
        aria-label={`Удалить ${name}`}
      >
        ×
      </button>
    </span>
  );
}

/** Пилюля голосового: play, ЭКГ-дорожка, длительность, ×. */
export function RVoicePill({
  length,
  src,
  onRemove,
}: {
  length: string;
  src?: string | null;
  onRemove: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = () => {
    if (!src) {
      return;
    }
    let el = audioRef.current;
    if (!el) {
      el = new Audio(src);
      el.onended = () => setPlaying(false);
      audioRef.current = el;
    }
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    void el.play().then(
      () => setPlaying(true),
      () => setPlaying(false)
    );
  };

  return (
    <span className="flex items-center gap-[10px] rounded-full bg-[#F1F3F8] px-[11px] py-[7px]">
      <button
        type="button"
        onClick={toggle}
        className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-[#57B792]"
        aria-label={playing ? 'Пауза' : 'Прослушать'}
      >
        {playing ? (
          <svg
            width="10"
            height="10"
            viewBox="0 0 16 16"
            fill="#FFFFFF"
            aria-hidden
          >
            <path d="M4.5 3h2.6v10H4.5zM8.9 3h2.6v10H8.9z" />
          </svg>
        ) : (
          <PlayGlyph size={10} />
        )}
      </button>
      <span className="min-w-0 flex-1">
        <svg
          viewBox="0 0 300 40"
          preserveAspectRatio="none"
          className="block h-[20px] w-full"
          aria-hidden
        >
          <polyline
            points={WAVE_POINTS}
            fill="none"
            stroke="rgba(46,62,106,0.22)"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </span>
      <span className="whitespace-nowrap text-[11px] font-bold text-[#141E31]">
        {length}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="text-[12px] text-[rgba(20,30,49,0.45)]"
        aria-label="Удалить голосовое"
      >
        ×
      </button>
    </span>
  );
}

/** Чип документа: иконка файла, имя, ×. */
export function RDocChip({
  name,
  onRemove,
}: {
  name: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-[7px] rounded-[9px] bg-white px-[10px] py-[5px] text-[11px] text-[#575757] shadow-[0_1px_3px_rgba(20,30,49,0.08)]">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#3B4D7F"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0"
        aria-hidden
      >
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5" />
      </svg>
      <span className="max-w-[220px] truncate">{name}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-[12px] text-[rgba(20,30,49,0.45)]"
        aria-label={`Удалить ${name}`}
      >
        ×
      </button>
    </span>
  );
}
